// Implements: F-UIX-019
/**
 * The platform admin's reads: the per-organization totals (S11a-3, RPT.5) and the
 * organization list that names them (`GET /api/organizations`).
 *
 * Both are `GET`s, so the client's ordinary policy applies. The totals are read only by
 * a platform admin: anybody else gets the `404` of a path that does not exist, which is
 * why the route guard decides first and this module is never reached by another role.
 */

import { httpDownload, httpRequest } from "../../../shared/api/httpClient";
import type { Download } from "../../../shared/api/httpClient";
import type { OrganizationListView, PlatformTotalsView } from "../../../types/reports";

/** The range the totals cover: `YYYY-MM-DD` days, or `""` for the server's default. */
export interface DayRange {
  readonly from: string;
  readonly to: string;
}

/** The server's own range: the 366 days ending today in the configured zone. */
export const DEFAULT_RANGE: DayRange = { from: "", to: "" };

export const ORGANIZATIONS_PATH = "/api/organizations";

const TOTALS_PATH = "/api/reports/platform/organizations";

/** Only the bounds that are set: an empty one is the server's own default. */
function rangeQuery(range: DayRange): URLSearchParams {
  const query = new URLSearchParams();
  if (range.from !== "") query.set("from", range.from);
  if (range.to !== "") query.set("to", range.to);
  return query;
}

/** `GET /reports/platform/organizations`, sending only the bounds that are set. */
export function platformTotalsPath(range: DayRange): string {
  const text = rangeQuery(range).toString();
  return `${TOTALS_PATH}${text === "" ? "" : `?${text}`}`;
}

/** The file name of a totals export whose answer names none: the server's stem (`export_name`). */
export const PLATFORM_TOTALS_EXPORT_NAME = "totales-plataforma.csv";

/** The totals' export twin, `GET /reports/platform/organizations/export`, as CSV. */
export function platformTotalsExportPath(range: DayRange): string {
  const query = rangeQuery(range);
  query.set("format", "csv");
  return `${TOTALS_PATH}/export?${query.toString()}`;
}

export function downloadPlatformTotals(range: DayRange): Promise<Download> {
  return httpDownload(platformTotalsExportPath(range));
}

export async function fetchPlatformTotals(
  range: DayRange,
  signal?: AbortSignal,
): Promise<PlatformTotalsView> {
  const body = await httpRequest<PlatformTotalsView>(platformTotalsPath(range), { signal });
  if (body === undefined) throw new Error("The platform totals answered no body.");
  return body;
}

export async function fetchOrganizations(signal?: AbortSignal): Promise<OrganizationListView> {
  const body = await httpRequest<OrganizationListView>(ORGANIZATIONS_PATH, { signal });
  return body ?? { organizations: [] };
}
